// Agente de hospedagem (HOSPEDAGEM_IA) — campos de venda dentro do jsonContent:
// escada de condições, períodos sem escada, ficha da empresa e abertura.
// Funções puras: convertem jsonContent <-> estado do formulário e validam antes
// de salvar (o backend descarta em silêncio degrau/período inválido).
// Contrato: backend/src/services/HospedagemServices/ai/concessionLadder.ts.

export const MAX_DEGRAUS = 4;
export const ESCADA_TIPOS = ["ISENTAR_LIMPEZA", "INCLUIR_EXTRA", "DESCONTO_PCT", "NOITE_EXTRA"];
export const PCT_MAX = { DESCONTO_PCT: 10, NOITE_EXTRA: 50 };

export const VENDEDORA_DEFAULTS = {
  responderPrimeiraMensagem: false,
  fichaEmpresa: "",
  escada: [],
  periodosSemEscada: [],
  incluirFeriadosNacionais: false,
};

export const emptyDegrau = () => ({ tipo: "", servicoExtraNome: "", pct: "", frase: "" });
export const emptyPeriodo = () => ({ anual: true, inicio: "", fim: "", rotulo: "" });

const str = (v) => (typeof v === "string" ? v : v === undefined || v === null ? "" : String(v));

const validIso = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return (
    d.getUTCFullYear() === Number(m[1]) &&
    d.getUTCMonth() === Number(m[2]) - 1 &&
    d.getUTCDate() === Number(m[3])
  );
};

/** "DD/MM" (aceita "D/M") -> "MM-DD"; null se inválido. 29/02 vale (ano bissexto). */
export const diaMesToMmDd = (s) => {
  const m = /^\s*(\d{1,2})\s*\/\s*(\d{1,2})\s*$/.exec(String(s || ""));
  if (!m) return null;
  const dd = m[1].padStart(2, "0");
  const mm = m[2].padStart(2, "0");
  return validIso(`2024-${mm}-${dd}`) ? `${mm}-${dd}` : null;
};

/** "MM-DD" -> "DD/MM" */
export const mmDdToDiaMes = (s) => {
  const m = /^(\d{2})-(\d{2})$/.exec(String(s || ""));
  return m ? `${m[2]}/${m[1]}` : "";
};

// ---------- jsonContent -> formulário ----------

export const escadaFromJson = (raw) =>
  (Array.isArray(raw) ? raw : [])
    .filter((r) => r && typeof r === "object")
    .map((r) => ({
      tipo: str(r.tipo),
      servicoExtraNome: str(r.servicoExtraNome),
      pct: r.pct === undefined || r.pct === null ? "" : String(r.pct),
      frase: str(r.frase),
    }));

export const periodosFromJson = (raw) =>
  (Array.isArray(raw) ? raw : [])
    .filter((r) => r && typeof r === "object")
    .map((r) => {
      const inicio = str(r.inicio);
      const fim = str(r.fim);
      const anual = /^\d{2}-\d{2}$/.test(inicio) || /^\d{2}-\d{2}$/.test(fim);
      return {
        anual,
        inicio: anual ? mmDdToDiaMes(inicio) || inicio : inicio,
        fim: anual ? mmDdToDiaMes(fim) || fim : fim,
        rotulo: str(r.rotulo),
      };
    });

/** Valores do formulário a partir do jsonContent já parseado (integração antiga = padrões). */
export const vendedoraFromJson = (json) => {
  const j = json && typeof json === "object" ? json : {};
  return {
    responderPrimeiraMensagem: j.responderPrimeiraMensagem === true,
    fichaEmpresa: typeof j.fichaEmpresa === "string" ? j.fichaEmpresa : "",
    escada: escadaFromJson(j.escada),
    periodosSemEscada: periodosFromJson(j.periodosSemEscada),
    incluirFeriadosNacionais: j.incluirFeriadosNacionais === true,
  };
};

// ---------- formulário -> jsonContent ----------

export const escadaToJson = (escada) =>
  (Array.isArray(escada) ? escada : []).map((d) => {
    const out = { tipo: d.tipo, frase: str(d.frase).trim() };
    if (d.tipo === "DESCONTO_PCT" || d.tipo === "NOITE_EXTRA") out.pct = Number(d.pct);
    if (d.tipo === "INCLUIR_EXTRA") out.servicoExtraNome = str(d.servicoExtraNome).trim();
    return out;
  });

export const periodosToJson = (periodos) =>
  (Array.isArray(periodos) ? periodos : []).map((p) => ({
    inicio: p.anual ? diaMesToMmDd(p.inicio) : str(p.inicio),
    fim: p.anual ? diaMesToMmDd(p.fim) : str(p.fim),
    rotulo: str(p.rotulo).trim(),
  }));

/**
 * Grava os campos de venda em `json` (cópia). Campo no padrão que não existia
 * no jsonContent original fica de fora: integração antiga salva sem mexer não muda.
 */
export const applyVendedoraFields = (json, values, baseJson = {}) => {
  const out = { ...json };
  const base = baseJson && typeof baseJson === "object" ? baseJson : {};
  const put = (key, value, isDefault) => {
    if (isDefault && !Object.prototype.hasOwnProperty.call(base, key)) delete out[key];
    else out[key] = value;
  };
  const responder = values.responderPrimeiraMensagem === true;
  put("responderPrimeiraMensagem", responder, !responder);
  const ficha = str(values.fichaEmpresa);
  put("fichaEmpresa", ficha, ficha.trim() === "");
  const escada = escadaToJson(values.escada);
  put("escada", escada, escada.length === 0);
  const periodos = periodosToJson(values.periodosSemEscada);
  put("periodosSemEscada", periodos, periodos.length === 0);
  const feriados = values.incluirFeriadosNacionais === true;
  put("incluirFeriadosNacionais", feriados, !feriados);
  return out;
};

// ---------- conector: proposta do agente ----------
// `propostaHabilitada` no jsonContent do HOSPEDAGEM_CONECTOR (padrão desligado). Só com ela o
// agente gera proposta; ligar só depois de o site publicar a rota de propostas.

export const conectorPropostaFromJson = (json) => Boolean(json && json.propostaHabilitada === true);

/** Liga grava true; desligado só é gravado (false) se a chave já existia — salvar sem mexer não muda. */
export const applyConectorPropostaField = (json, values, baseJson = {}) => {
  const out = { ...json };
  const base = baseJson && typeof baseJson === "object" ? baseJson : {};
  if (values.propostaHabilitada === true) out.propostaHabilitada = true;
  else if (Object.prototype.hasOwnProperty.call(base, "propostaHabilitada")) out.propostaHabilitada = false;
  else delete out.propostaHabilitada;
  return out;
};

// ---------- validação ----------
// Devolve chaves de tradução (queueIntegrationModal.hospedagem.errors.*) por campo.

export const validateVendedora = (values) => {
  const escadaErrors = (Array.isArray(values.escada) ? values.escada : []).map((d, i) => {
    const e = {};
    if (i >= MAX_DEGRAUS) e.tipo = "maxDegraus";
    if (!ESCADA_TIPOS.includes(d.tipo)) e.tipo = e.tipo || "tipoRequired";
    if (d.tipo === "DESCONTO_PCT" || d.tipo === "NOITE_EXTRA") {
      const raw = str(d.pct).trim();
      const n = Number(raw);
      if (!/^\d+$/.test(raw) || !Number.isInteger(n) || n < 1 || n > PCT_MAX[d.tipo]) {
        e.pct = d.tipo === "DESCONTO_PCT" ? "pctDesconto" : "pctNoite";
      }
    }
    if (d.tipo === "INCLUIR_EXTRA" && !str(d.servicoExtraNome).trim()) e.servicoExtraNome = "extraRequired";
    if (!str(d.frase).trim()) e.frase = "fraseRequired";
    return Object.keys(e).length ? e : null;
  });

  const periodoErrors = (Array.isArray(values.periodosSemEscada) ? values.periodosSemEscada : []).map((p) => {
    const e = {};
    if (!str(p.rotulo).trim()) e.rotulo = "rotuloRequired";
    if (p.anual) {
      if (!diaMesToMmDd(p.inicio)) e.inicio = "diaMesInvalid";
      if (!diaMesToMmDd(p.fim)) e.fim = "diaMesInvalid";
    } else {
      if (!validIso(p.inicio)) e.inicio = "dataInvalid";
      if (!validIso(p.fim)) e.fim = "dataInvalid";
      if (!e.inicio && !e.fim && p.fim < p.inicio) e.fim = "fimAntesInicio";
    }
    return Object.keys(e).length ? e : null;
  });

  const hasErrors = escadaErrors.some(Boolean) || periodoErrors.some(Boolean);
  return { escada: escadaErrors, periodos: periodoErrors, hasErrors };
};
